import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { useCart } from "../contexts/CartContext";
import { Button } from "../components/common/Button";
import {
  PaymentMethodSelector,
  PaymentMethod,
} from "../components/common/PaymentMethodSelector";
import {
  ArrowLeft,
  Package,
  Gift,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { CopyableCode } from "../components/common/CopyableCode";
import styles from "./CheckoutPage.module.css";

// Flat fee added per order when marked as a gift — each cart item becomes
// its own order, so this applies once per item. Kept in sync with the
// backend's GIFT_FEE (src/utils/giftFee.js); the server always computes the
// real charge, this is just for an accurate pre-checkout estimate.
const GIFT_FEE = 40;

interface FormData {
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  deliveryAddress: string;
  governorate: string;
  area: string;
  isGift: boolean;
  giftMessage: string;
  paymentMethod: PaymentMethod;
}

interface FormErrors {
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  deliveryAddress?: string;
  governorate?: string;
}

interface Governorate {
  _id: string;
  name: string;
  shippingCost: number;
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const { items, getCartTotal, clearCart } = useCart();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [governorates, setGovernorates] = useState<Governorate[]>([]);
  const [referenceNumber, setReferenceNumber] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    guestName: "",
    guestEmail: "",
    guestPhone: "",
    deliveryAddress: "",
    governorate: "",
    area: "",
    isGift: false,
    giftMessage: "",
    paymentMethod: "cod",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    const fetchGovernorates = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/governorates`);
        const data = await response.json();
        setGovernorates(data.governorates || []);
      } catch (error) {
        console.error("Error fetching governorates:", error);
      }
    };

    fetchGovernorates();
  }, []);

  useEffect(() => {
    // The success screen replaces the whole checkout form in place, but the
    // page is still scrolled to wherever the "Place Order" button was —
    // bring the confirmation into view instead of leaving it off-screen.
    if (submitStatus === "success") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [submitStatus]);

  const shippingCost =
    governorates.find((g) => g.name === formData.governorate)?.shippingCost || 0;
  const giftFeeTotal = formData.isGift ? GIFT_FEE * items.length : 0;
  const orderTotal = getCartTotal() + shippingCost + giftFeeTotal;

  // Redirect to cart if empty
  if (items.length === 0 && submitStatus !== "success") {
    navigate("/cart");
    return null;
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.guestName.trim()) {
      newErrors.guestName = "Name is required";
    }

    if (!formData.guestEmail.trim()) {
      newErrors.guestEmail = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guestEmail)) {
      newErrors.guestEmail = "Please enter a valid email";
    }

    if (!formData.guestPhone.trim()) {
      newErrors.guestPhone = "Phone number is required";
    } else if (!/^0\d{10}$/.test(formData.guestPhone)) {
      newErrors.guestPhone =
        "Please enter a valid Egyptian phone number (11 digits starting with 0)";
    }

    if (!formData.deliveryAddress.trim()) {
      newErrors.deliveryAddress = "Delivery address is required";
    }

    if (!formData.governorate) {
      newErrors.governorate = "Governorate is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      // Submit the whole cart in one request — the backend creates one order
      // document per item but shares a single reference number and sends a
      // single consolidated confirmation email across all of them, so the
      // customer only ever has to track one code for the checkout.
      const response = await fetch(`${API_BASE_URL}/api/checkout/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            type: item.type === "palette" ? "palette" : "kit",
            id: item.id,
            quantity: item.quantity,
          })),
          guestName: formData.guestName,
          guestEmail: formData.guestEmail,
          guestPhone: formData.guestPhone,
          deliveryAddress: formData.deliveryAddress,
          governorate: formData.governorate,
          area: formData.area,
          isGift: formData.isGift,
          giftMessage: formData.isGift ? formData.giftMessage : "",
          paymentMethod: formData.paymentMethod,
        }),
      });

      if (!response.ok) {
        throw new Error("Checkout failed");
      }

      const result = await response.json();
      setReferenceNumber(result.referenceNumber || null);
      setSubmitStatus("success");
      clearCart();
    } catch (error) {
      console.error("Error submitting order:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === "success") {
    return (
      <div className={styles.checkoutPage}>
        <div className={styles.container}>
          <div className={styles.successMessage}>
            <CheckCircle size={64} className={styles.successIcon} />
            <h2>Order Placed Successfully!</h2>
            <p>
              Thank you for your order. We'll contact you shortly via Whatsapp.
            </p>
            {referenceNumber && (
              <div className={styles.referenceSection}>
                <p className={styles.referenceLabel}>Your reference number</p>
                <CopyableCode value={referenceNumber} />
                <p className={styles.referenceHint}>
                  Save this — quote it if you need to ask us about your order.
                </p>
              </div>
            )}
            {formData.paymentMethod !== "cod" && (
              <p className={styles.emailNote}>
                Don't forget to send your payment screenshot on WhatsApp so we
                can confirm your order.
              </p>
            )}
            {/* <p className={styles.emailNote}>Please check your email at <strong>{formData.guestEmail}</strong></p> */}
            <Button
              variant="primary"
              onClick={() => navigate("/products/kits")}
            >
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.checkoutPage}>
      <div className={styles.container}>
        <button onClick={() => navigate("/cart")} className={styles.backButton}>
          <ArrowLeft size={20} />
          Back to Cart
        </button>

        <h1 className={styles.title}>Checkout</h1>

        <div className={styles.checkoutContent}>
          {/* Checkout Form */}
          <form onSubmit={handleSubmit} className={styles.checkoutForm}>
            <div className={styles.formSection}>
              <h2>Contact Information</h2>

              <div className={styles.formGroup}>
                <label htmlFor="guestName">Full Name *</label>
                <input
                  type="text"
                  id="guestName"
                  name="guestName"
                  value={formData.guestName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className={errors.guestName ? styles.inputError : ""}
                />
                {errors.guestName && (
                  <span className={styles.error}>{errors.guestName}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="guestEmail">Email Address *</label>
                <input
                  type="email"
                  id="guestEmail"
                  name="guestEmail"
                  value={formData.guestEmail}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  className={errors.guestEmail ? styles.inputError : ""}
                />
                {errors.guestEmail && (
                  <span className={styles.error}>{errors.guestEmail}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="guestPhone">Phone Number *</label>
                <input
                  type="tel"
                  id="guestPhone"
                  name="guestPhone"
                  value={formData.guestPhone}
                  onChange={handleInputChange}
                  placeholder="e.g., 01012345678"
                  className={errors.guestPhone ? styles.inputError : ""}
                />
                {errors.guestPhone && (
                  <span className={styles.error}>{errors.guestPhone}</span>
                )}
              </div>
            </div>

            <div className={styles.formSection}>
              <h2>Delivery Address</h2>

              <div className={styles.formGroup}>
                <label htmlFor="governorate">Governorate *</label>
                <select
                  id="governorate"
                  name="governorate"
                  value={formData.governorate}
                  onChange={handleInputChange}
                  className={errors.governorate ? styles.inputError : ""}
                >
                  <option value="">Select your governorate</option>
                  {governorates.map((gov) => (
                    <option key={gov._id} value={gov.name}>
                      {gov.name} — EGP {gov.shippingCost}
                    </option>
                  ))}
                </select>
                {errors.governorate && (
                  <span className={styles.error}>{errors.governorate}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="area">Area (Optional)</label>
                <input
                  type="text"
                  id="area"
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  placeholder="e.g., Nasr City, Maadi..."
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="deliveryAddress">Street Address *</label>
                <textarea
                  id="deliveryAddress"
                  name="deliveryAddress"
                  value={formData.deliveryAddress}
                  onChange={handleInputChange}
                  placeholder="Building, street, and any landmarks"
                  rows={3}
                  className={errors.deliveryAddress ? styles.inputError : ""}
                />
                {errors.deliveryAddress && (
                  <span className={styles.error}>{errors.deliveryAddress}</span>
                )}
              </div>
            </div>

            <div className={styles.formSection}>
              <h2>Payment Method</h2>
              <PaymentMethodSelector
                value={formData.paymentMethod}
                onChange={(paymentMethod) =>
                  setFormData((prev) => ({ ...prev, paymentMethod }))
                }
              />
            </div>

            <div className={styles.formSection}>
              <h2>Gift Options</h2>

              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="isGift"
                  checked={formData.isGift}
                  onChange={handleInputChange}
                />
                <Gift size={18} />
                <span>
                  This is a gift{" "}
                  <span className={styles.giftFeeNote}>
                    (+EGP {GIFT_FEE}{items.length > 1 ? " per item" : ""})
                  </span>
                </span>
              </label>

              {formData.isGift && (
                <div className={styles.formGroup}>
                  <label htmlFor="giftMessage">Gift Message (Optional)</label>
                  <textarea
                    id="giftMessage"
                    name="giftMessage"
                    value={formData.giftMessage}
                    onChange={handleInputChange}
                    placeholder="Add a personal message for the recipient..."
                    rows={3}
                  />
                </div>
              )}
            </div>

            {submitStatus === "error" && (
              <div className={styles.errorMessage}>
                <AlertCircle size={20} />
                <span>Something went wrong. Please try again.</span>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              className={styles.submitBtn}
            >
              {isSubmitting
                ? "Placing Order..."
                : `Place Order - EGP ${orderTotal}`}
            </Button>
          </form>

          {/* Order Summary */}
          <div className={styles.orderSummary}>
            <h2>Order Summary</h2>
            <div className={styles.orderItems}>
              {items.map((item) => (
                <div key={item.id} className={styles.orderItem}>
                  <div className={styles.orderItemImage}>
                    <img src={item.imageUrl} alt={item.name} />
                    <span className={styles.itemQuantity}>{item.quantity}</span>
                  </div>
                  <div className={styles.orderItemDetails}>
                    <h4>{item.name}</h4>
                    {item.size && <p>{item.size}</p>}
                  </div>
                  <span className={styles.orderItemPrice}>
                    EGP {item.price * item.quantity}
                  </span>
                </div>
              ))}
            </div>
            <div className={styles.summaryRow}>
              <span>Subtotal</span>
              <span>EGP {getCartTotal()}</span>
            </div>
            <div className={styles.summaryRow}>
              <span>Shipping{formData.governorate ? ` (${formData.governorate})` : ""}</span>
              <span>{formData.governorate ? `EGP ${shippingCost}` : "Select governorate"}</span>
            </div>
            {formData.isGift && (
              <div className={styles.summaryRow}>
                <span>
                  Gift Fee{items.length > 1 ? ` (${items.length} × EGP ${GIFT_FEE})` : ""}
                </span>
                <span>EGP {giftFeeTotal}</span>
              </div>
            )}
            <div className={styles.summaryTotal}>
              <span>Total</span>
              <span>EGP {orderTotal}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
